import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import MultiSearchTerms from './MultiSearchTerms';

export default class MultiSearch extends PureComponent {
  static propTypes = {
    betaFeatures: PropTypes.array.isRequired,
    buttonText: PropTypes.string,
    searchTerms: PropTypes.arrayOf(PropTypes.string),
    error: PropTypes.string,
    search: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    loadSearchTerms: PropTypes.func.isRequired,
    updateSearchTermsError: PropTypes.func.isRequired,
  };

  static defaultProps = {
    buttonText: 'List search',
  }

  state = {
    isEditable: !this.props.searchTerms || !this.props.searchTerms.length,
    // We don't need to render dropdown content until the user activates it for the first time
    isRendered: false,
    isOpen: false,
    searchTerms: this.props.searchTerms ? this.props.searchTerms.join('\n') : '',
  };

  componentDidMount() {
    this.props.loadSearchTerms();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.searchTerms !== this.props.searchTerms) {
      this.setState({
        isEditable: !nextProps.searchTerms || !nextProps.searchTerms.length,
        searchTerms: nextProps.searchTerms ? nextProps.searchTerms.join('\n') : '',
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.collapseIfClickedOutside);
  }

  onClickClear = (event) => {
    if (event) {
      event.preventDefault();
    }

    this.clickedInside = true;
    this.props.clear().then(() => {
      this.setState({ isEditable: true });
    });
  };

  onClickEdit = (event) => {
    if (event) {
      event.preventDefault();
    }

    this.clickedInside = true;

    this.setState({ isEditable: true });
  };

  onClickTryAgain = (event) => {
    if (event) {
      event.preventDefault();
    }

    this.props.updateSearchTermsError({ error: null });
  };

  onChange = (event) => {
    const searchTerms = event.target.value;

    this.setState({ searchTerms });
  };

  onSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }

    this.setState({ isEditable: false });
    this.props.search(this.searchTermsArray);
  };

  get searchTermsArray() {
    return this.state.searchTerms
      .replace(/^\s+|\s+$/g, '')
      .replace(/[\n\r]+/g, ',')
      .split(',')
      .map(term => term.trim())
      .filter(Boolean);
  }

  expand = () => {
    this.setState({
      isRendered: true,
      isOpen: true,
    });

    document.addEventListener('click', this.collapseIfClickedOutside);
  };

  collapse = () => {
    this.setState({ isOpen: false });

    document.removeEventListener('click', this.collapseIfClickedOutside);
  };

  collapseIfClickedOutside = (event) => {
    event.stopPropagation();

    const clickedInside = (
      this.clickedInside || (
        this.container
        && this.container.contains(event.target)
      )
    );

    if (!clickedInside) {
      this.collapse();
    }
  };

  toggle = () => (
    this.state.isOpen
      ? this.collapse()
      : this.expand()
  );

  renderEditForm() {
    const { searchTerms } = this.state;

    return (
      <form onSubmit={this.onSubmit}>
        <div className="hd-search__multiSearch__dropdown__textareaRow">
          <textarea
            className="hd-search__multiSearch__dropdown__textareaField"
            cols="36"
            rows="9"
            onChange={this.onChange}
            placeholder="Type in a list of products"
            title="Type in a list of products"
            value={searchTerms || ''}
          />
        </div>
        <button
          type="submit"
          className="btn-primary hd-search__multiSearch__dropdown__submitButton"
        >
          Search
        </button>
      </form>
    );
  }

  renderTerms() {
    return (
      <div className="hd-search__multiSearch__dropdown--terms">
        <MultiSearchTerms searchTerms={this.searchTermsArray} />
        <a
          href="/webshop/performMultiSearch.do?type=consolidatedMulti&searchPageIndex=0"
          className="hd-search__multiSearch__dropdown--termsBack"
        >
          Back to combined results
        </a>
        <button
          type="button"
          onClick={this.onClickEdit}
          className="button-link hd-search__multiSearch__dropdown--termsEdit"
        >
          Edit list
        </button>
        <button
          type="button"
          onClick={this.onClickClear}
          className="button-link hd-search__multiSearch__dropdown--termsClear"
        >
          Clear list
        </button>
      </div>
    );
  }

  renderDropdown() {
    const { isRendered } = this.state;

    if (!isRendered) {
      return null;
    }

    const { error } = this.props;
    const { isEditable, isOpen } = this.state;

    const className = 'hd-search__multiSearch__dropdown';

    return (
      <div
        className={classNames(
          className,
          { [`${className}--open`]: isOpen }
        )}
        ref={ref => { this.container = ref; }}
      >
        <button
          className="hd-search__multiSearch__dropdown__closeButton"
          onClick={this.collapse}
          aria-label="Close"
        />
        <h2 className="hd-search__multiSearch__dropdown__header">
          Type in a list of products and we will find them for you.
        </h2>
        {error ? (
          <div className="hd-search__multiSearch__dropdown--error">
            {error}
            <button
              onClick={this.onClickTryAgain}
              className="hd-search__multiSearch__dropdown--errorGoBack"
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="hd-search__multiSearch__dropdown__content">
            <div className="hd-search__multiSearch__dropdown--searchTerms">
              {isEditable
                ? this.renderEditForm()
                : this.renderTerms()
              }
            </div>
            <div>
              <p className="hd-search__multiSearch__dropdown--description">
                Finding products with a list is really easy.{' '}
                Simply type in an item and hit the &quot;Enter&quot; button after each one.
              </p>
              <p className="hd-search__multiSearch__dropdown--description">
                Once you&apos;ve made your list, we can use it to find all the items that you want.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { buttonText } = this.props;

    return (
      <div className={classNames(
        'hd-search__multiSearch',
        'hd-searchProminent__multiSearch',
      )}
      >
        <button
          type="button"
          className={classNames(
            'hd-search__multiSearch__button',
            'hd-searchProminent__multiSearch__button',
          )}
          aria-label="Find a list of products"
          onClick={this.toggle}
        >
          { buttonText }
        </button>
        {this.renderDropdown()}
      </div>
    );
  }
}
